import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificacaoService, type Notificacao } from '../services/notificacaoService';

const CONTAGEM_KEY = ['notificacoes', 'contagem'] as const;
const LISTA_KEY = ['notificacoes', 'lista'] as const;

function formatarData(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const NotificationBell: React.FC<{ saas?: boolean }> = ({ saas = false }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const contagem = useQuery({
    queryKey: CONTAGEM_KEY,
    queryFn: notificacaoService.contarNaoLidas,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const lista = useQuery({
    queryKey: LISTA_KEY,
    queryFn: () => notificacaoService.listar(0, 20),
    enabled: open,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['notificacoes'] });
  const marcarLida = useMutation({ mutationFn: notificacaoService.marcarLida, onSuccess: invalidar });
  const marcarTodas = useMutation({ mutationFn: notificacaoService.marcarTodasLidas, onSuccess: invalidar });

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const naoLidas = contagem.data ?? 0;

  const abrir = (n: Notificacao) => {
    if (!n.lida) marcarLida.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const buttonClass = saas
    ? 'border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={naoLidas > 0 ? `Notificações: ${naoLidas} não lidas` : 'Notificações'}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`relative flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border transition ${buttonClass}`}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {naoLidas > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white">
            {naoLidas > 99 ? '99+' : naoLidas}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Central de notificações"
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900"
        >
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5 dark:border-slate-700">
            <span className="text-sm font-bold text-slate-900 dark:text-slate-100">Notificações</span>
            <button
              type="button"
              disabled={naoLidas === 0 || marcarTodas.isPending}
              onClick={() => marcarTodas.mutate()}
              className="text-xs font-semibold text-blue-600 hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline dark:text-blue-400"
            >
              Marcar todas como lidas
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {lista.isLoading && (
              <p className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">Carregando...</p>
            )}
            {lista.isError && (
              <p className="px-4 py-6 text-center text-sm text-red-600 dark:text-red-400">
                Não foi possível carregar as notificações.
              </p>
            )}
            {lista.data && lista.data.itens.length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
                Você não tem notificações.
              </p>
            )}
            {lista.data?.itens.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => abrir(n)}
                className={`flex w-full items-start gap-2.5 border-b border-slate-100 px-4 py-3 text-left transition last:border-b-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800 ${
                  n.lida ? '' : 'bg-blue-50/60 dark:bg-blue-500/10'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.lida ? 'bg-transparent' : 'bg-blue-600'}`}
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={`block truncate text-sm text-slate-900 dark:text-slate-100 ${n.lida ? 'font-medium' : 'font-bold'}`}
                  >
                    {n.titulo}
                  </span>
                  {n.mensagem && (
                    <span className="mt-0.5 line-clamp-2 block text-xs text-slate-600 dark:text-slate-300">{n.mensagem}</span>
                  )}
                  <span className="mt-1 block text-[11px] text-slate-400">{formatarData(n.criadaEm)}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
